<template>
  <div class="v2-brand-cart">
    <!-- Loading -->
    <div
      v-if="isLoadingCart"
      class="v2-brand-cart__loading"
    >
      <v2-spinner
        size="lg"
        label="Loading cart..."
      />
    </div>

    <!-- Retailer selector / selected display (hidden while loading) -->
    <template v-if="!isLoadingCart">
      <!-- Retailer selector (when multiple direct retailers and none pre-selected) -->
      <div
        v-if="directRetailers.length && !selectedDirectRetailer"
        class="v2-brand-cart__retailer-select"
      >
        <span class="v2-brand-cart__label">
          Select Retailer
          <span class="v2-brand-cart__required">*</span>
        </span>
        <v2-dropdown
          :value="selectedRetailer"
          :options="allDirectRetailers"
          :searchable="true"
          label-key="_displayLabel"
          placeholder="Select Retailer"
          @search="onRetailerSearch"
          @select="onSelectRetailer"
        >
          <template #menu="{ close }">
            <div class="v2-dropdown__search">
              <input
                v-model="searchText"
                class="v2-dropdown__search-input"
                placeholder="Search retailer"
                @input="searchRetailer"
              >
            </div>
            <div
              v-if="allDirectRetailers.length"
              class="v2-dropdown__options"
            >
              <button
                v-for="option in allDirectRetailers"
                :key="option.retailerId"
                class="v2-dropdown__option"
                :class="{ 'v2-dropdown__option--active': option.retailerId === retailerId }"
                @click="onSelectRetailer(option); close()"
              >
                {{ option.entityName }} — {{ option.email }}
              </button>
            </div>
            <div
              v-else
              class="v2-dropdown__empty"
            >
              No matched retailers
            </div>
          </template>
        </v2-dropdown>
        <div class="v2-brand-cart__row-actions">
          <v2-button
            variant="outline"
            size="sm"
            @click="openImportModal"
          >
            Import
          </v2-button>
          <v2-button
            variant="outline"
            size="sm"
            @click="openExportModal"
          >
            Export
          </v2-button>
        </div>
      </div>

      <!-- Selected retailer display -->
      <div
        v-else-if="selectedDirectRetailer"
        class="v2-brand-cart__retailer-selected"
      >
        <span>Selected Retailer:</span>
        <strong>{{ selectedDirectRetailerTitle }}</strong>
        <div class="v2-brand-cart__row-actions">
          <v2-button
            variant="outline"
            size="sm"
            @click="openImportModal"
          >
            Import
          </v2-button>
          <v2-button
            variant="outline"
            size="sm"
            @click="openExportModal"
          >
            Export
          </v2-button>
        </div>
      </div>
    </template>

    <!--
      Cart Total: kept mounted at all times via v-show (NOT v-if).
      Using v-if/v-else here would destroy and recreate the component on every
      retailer change (isLoadingCart toggle), wiping user-edited quantities,
      notes, acceptTerms, and other local state.
    -->
    <v2-cart-total
      v-show="!isLoadingCart"
      ref="cartTotal"
      :cart-products="cartProducts"
      :retailer-id="retailerId"
      :brand-notes="notesData"
      is-popup
      :modal-id="modalId"
      :get-cart-products-by-brand="getCartProductsByBrand"
      @remove-product-selected-from-brand="handleRemoveProduct"
      @update-notes="updateNotes"
      @remove-multiple-products="handleRemoveMultipleProducts"
    />
  </div>
</template>

<script>
import { onMounted } from '@vue/composition-api'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import { useBrandCart } from '@/composables/useBrandCart'
import V2CartTotal from './V2CartTotal.vue'

export default {
  name: 'V2BrandCart',
  components: {
    V2Spinner,
    V2Button,
    V2Dropdown,
    V2CartTotal,
  },
  props: {
    cartProducts: {
      type: Array,
      default: () => [],
    },
    getCartProductsByBrand: {
      type: Function,
      default: () => {},
    },
    isBrandCartUpdated: {
      type: Number,
      default: 0,
    },
    isLoadingCart: {
      type: Boolean,
      default: false,
    },
    modalId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const brandCart = useBrandCart(props)

    onMounted(() => {
      brandCart.initRetailers()
    })

    return { ...brandCart }
  },
  watch: {
    retailerId(val) {
      // The refetch below returns the same product IDs with fresh pricing
      // (pricebook / currency / discount). V2CartProducts' watcher skips
      // re-init when the ID set is unchanged, so force a re-init — otherwise
      // the cart keeps rendering the previous retailer's (or raw) prices.
      this.$refs.cartTotal?.$refs?.cartProducts?.scheduleReinitialize?.()
      this.$emit('get-full-selected-products', {
        retailerId: val || null,
        productIds: this.cartProducts.map(p => p._id) || [],
      })
    },
    searchText(val) {
      if (val) this.debouncedTrackSearchRetailer(val)
    },
  },
  emits: ['close-brand-cart-modal', 'get-full-selected-products'],
  methods: {
    onRetailerSearch(text) {
      this.searchText = text
      this.searchRetailer()
    },
    handleRemoveProduct(product) {
      const isEmpty = this.removeProduct(product)
      if (isEmpty) this.$emit('close-brand-cart-modal')
    },
    handleRemoveMultipleProducts(productIds) {
      const isEmpty = this.removeMultipleProducts(productIds)
      if (isEmpty) this.$emit('close-brand-cart-modal')
    },
    openImportModal() {
      this.$refs.cartTotal?.openImportModal()
    },
    openExportModal() {
      this.$refs.cartTotal?.onClickExportCart()
    },
  },
}
</script>
